import React, { Component, ErrorInfo, ReactNode } from 'react'
import {
  useNavigate,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router-dom'
import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { AppIcons } from '@/components/AppIcons'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/hooks/useTheme'

interface ErrorPageProps {
  error?: Error | null
  errorInfo?: ErrorInfo | null
  resetError?: () => void
  type?: 'error-boundary' | 'route-error' | '404'
}

// Functional component for route errors and 404s
export const ErrorPageContent: React.FC<ErrorPageProps> = ({
  error,
  resetError,
  type = 'route-error',
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const theme = useTheme()
  const routeError = type === 'route-error' ? useRouteError() : null

  // Determine error type and content
  let icon = <AppIcons name="warning" size={40} color="#FF3B30" />
  let title = t(
    'translation:error.somethingWentWrongTitle',
    'Oops! Something went wrong',
  )
  let message = t(
    'translation:error.somethingWentWrongMessage',
    "We're sorry for the inconvenience. An unexpected error occurred while loading this page.",
  )
  // let showError = process.env.NODE_ENV === 'development'
  // let errorDetails = error?.toString() || ''

  if (
    type === '404' ||
    (routeError &&
      isRouteErrorResponse(routeError) &&
      routeError.status === 404)
  ) {
    icon = <AppIcons name="search" size={40} color="#FF3B30" />
    title = t('translation:error.pageNotFoundTitle', 'Page Not Found')
    message = t(
      'translation:error.pageNotFoundMessage',
      "Sorry, the page you're looking for doesn't exist. It may have been moved or deleted.",
    )
    // showError = false
  } else if (routeError) {
    // errorDetails =
    //   routeError instanceof Error
    //     ? routeError.message
    //     : JSON.stringify(routeError, null, 2)
  }

  const handleGoHome = () => {
    navigate('/dashboard')
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const handleRefresh = () => {
    if (resetError) {
      resetError()
    } else {
      window.location.reload()
    }
  }

  return (
    <BaseView
      flex={1}
      center
      bgColor="background.screen"
      px={20}
      py={40}
      style={{ minHeight: '100vh' }}
    >
      <BaseView
        align="center"
        gap={24}
        style={{ maxWidth: '400px', width: '100%' }}
      >
        <BaseView
          center
          style={{
            width: '100px',
            height: '100px',
            borderRadius: '50px',
            backgroundColor: theme.colors.background.secondaryGray,
          }}
        >
          <BaseText fontSize={48} style={{ opacity: 0.8 }}>
            {icon}
          </BaseText>
        </BaseView>

        <BaseView align="center" gap={12}>
          <BaseText
            fontSize={24}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
          >
            {title}
          </BaseText>
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
            textAlign="center"
            style={{ lineHeight: 1.5 }}
          >
            {message}
          </BaseText>
        </BaseView>

        {/* {showError && errorDetails && (
          <BaseView
            p={16}
            bgColor="background.card"
            style={{
              borderRadius: '12px',
              width: '100%',
              border: '1px solid rgba(255, 59, 48, 0.2)',
            }}
          >
            <BaseText
              fontSize={12}
              fontFamily="regular"
              color="label.default"
              style={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
              }}
            >
              {errorDetails}
            </BaseText>
          </BaseView>
        )} */}

        <BaseView gap={12} style={{ width: '100%' }}>
          {type === '404' ? (
            <>
              <AppButton
                label={t('translation:error.goToHome', 'Go to Dashboard')}
                onPress={handleGoHome}
                type="primaryGradient"
                size="large"
                style={{ width: '100%' }}
              >
                {t('translation:error.goToHome', 'Go to Dashboard')}
              </AppButton>
              <AppButton
                label={t('translation:error.goBack', 'Go Back')}
                onPress={handleGoBack}
                type="secondaryGray"
                size="large"
                style={{ width: '100%' }}
              >
                {t('translation:error.goBack', 'Go Back')}
              </AppButton>
            </>
          ) : (
            <AppButton
              label={t('translation:error.refreshPage', 'Refresh Page')}
              onPress={handleRefresh}
              type="primaryGradient"
              size="large"
              style={{ width: '100%' }}
            >
              {t('translation:error.refreshPage', 'Refresh Page')}
            </AppButton>
          )}
        </BaseView>
      </BaseView>
    </BaseView>
  )
}

// Class component for error boundary
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(
    error: Error,
  ): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error,
      errorInfo,
    })
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorPageContent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.handleReset}
          type="error-boundary"
        />
      )
    }

    return this.props.children
  }
}

// Router error boundary component
export const RouterErrorBoundary: React.FC = () => {
  return <ErrorPageContent type="route-error" />
}

// 404 page component
export const NotFoundPage: React.FC = () => {
  return <ErrorPageContent type="404" />
}
